import React, { useState } from 'react';
import { SearchIcon, FilterIcon, CheckCircleIcon, LockIcon, ZapIcon, FileTextIcon, EyeIcon } from 'lucide-react';
export function IntegrationsCatalogTab() {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedCapability, setSelectedCapability] = useState<string>('all');
  const categories = ['All', 'CRM', 'MAP', 'Ads', 'Data', 'Messaging', 'Meetings', 'Support', 'Enrichment', 'Billing', 'Files', 'Generic'];
  const integrations = [{
    id: 1,
    name: 'Salesforce',
    category: 'CRM',
    description: 'Sync accounts, contacts, leads, and opportunities',
    logo: '☁️',
    badges: ['Read', 'Write', 'Near Real-time', 'Certified'],
    requiredPlan: 'Free',
    status: 'available'
  }, {
    id: 2,
    name: 'HubSpot',
    category: 'CRM',
    description: 'Connect CRM and marketing automation',
    logo: '🟠',
    badges: ['Read', 'Write', 'Near Real-time', 'Certified'],
    requiredPlan: 'Free',
    status: 'available'
  }, {
    id: 3,
    name: 'Marketo',
    category: 'MAP',
    description: 'Marketing automation and lead management',
    logo: '🔷',
    badges: ['Read', 'Write', 'Certified'],
    requiredPlan: 'Growth',
    status: 'available'
  }, {
    id: 4,
    name: 'Google Ads',
    category: 'Ads',
    description: 'Sync campaigns and audience data',
    logo: '🔍',
    badges: ['Read', 'Write'],
    requiredPlan: 'Growth',
    status: 'available'
  }, {
    id: 5,
    name: 'LinkedIn Ads',
    category: 'Ads',
    description: 'B2B advertising and audience sync',
    logo: '💼',
    badges: ['Read', 'Write'],
    requiredPlan: 'Growth',
    status: 'available'
  }, {
    id: 6,
    name: 'Snowflake',
    category: 'Data',
    description: 'Data warehouse integration',
    logo: '❄️',
    badges: ['Read', 'Write', 'Certified'],
    requiredPlan: 'Scale',
    status: 'available'
  }, {
    id: 7,
    name: 'Slack',
    category: 'Messaging',
    description: 'Notifications and workflow triggers',
    logo: '💬',
    badges: ['Write', 'Near Real-time'],
    requiredPlan: 'Free',
    status: 'available'
  }, {
    id: 8,
    name: 'Zoom',
    category: 'Meetings',
    description: 'Meeting data and webinar integration',
    logo: '📹',
    badges: ['Read'],
    requiredPlan: 'Growth',
    status: 'available'
  }, {
    id: 9,
    name: 'Zendesk',
    category: 'Support',
    description: 'Customer support tickets and data',
    logo: '🎫',
    badges: ['Read', 'Write'],
    requiredPlan: 'Growth',
    status: 'available'
  }, {
    id: 10,
    name: 'Segment',
    category: 'Enrichment',
    description: 'Customer data platform integration',
    logo: '📊',
    badges: ['Read', 'Write', 'Near Real-time'],
    requiredPlan: 'Scale',
    status: 'available'
  }, {
    id: 11,
    name: 'Stripe',
    category: 'Billing',
    description: 'Payment and subscription data',
    logo: '💳',
    badges: ['Read', 'Near Real-time'],
    requiredPlan: 'Growth',
    status: 'available'
  }, {
    id: 12,
    name: 'Google Drive',
    category: 'Files',
    description: 'Document storage and sharing',
    logo: '📁',
    badges: ['Read', 'Write'],
    requiredPlan: 'Free',
    status: 'available'
  }, {
    id: 13,
    name: 'REST API',
    category: 'Generic',
    description: 'Custom REST API integration',
    logo: '🔌',
    badges: ['Read', 'Write'],
    requiredPlan: 'Scale',
    status: 'available'
  }, {
    id: 14,
    name: 'Webhook',
    category: 'Generic',
    description: 'Inbound webhook receiver',
    logo: '🪝',
    badges: ['Write', 'Near Real-time'],
    requiredPlan: 'Growth',
    status: 'available'
  }];
  const filteredIntegrations = integrations.filter(integration => {
    if (selectedCategory !== 'all' && integration.category !== selectedCategory) {
      return false;
    }
    return true;
  });
  const getBadgeColor = (badge: string) => {
    switch (badge) {
      case 'Read':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Write':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'Near Real-time':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'Certified':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'Beta':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };
  return <div className="space-y-6" data-id="element-1573">
      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1574">
        <div className="flex items-center gap-4" data-id="element-1575">
          <FilterIcon className="w-5 h-5 text-gray-600" data-id="element-1576" />
          <div className="flex items-center gap-2 flex-wrap" data-id="element-1577">
            <span className="text-sm font-medium text-gray-700" data-id="element-1578">Category:</span>
            {categories.map(category => <button key={category} onClick={() => setSelectedCategory(category.toLowerCase())} className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${selectedCategory === category.toLowerCase() ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1579">
                {category}
              </button>)}
          </div>
        </div>
      </div>
      {/* Integration Cards Grid */}
      <div className="grid grid-cols-3 gap-6" data-id="element-1580">
        {filteredIntegrations.map(integration => <div key={integration.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow" data-id="element-1581">
            <div className="flex items-start justify-between mb-4" data-id="element-1582">
              <div className="flex items-center gap-3" data-id="element-1583">
                <div className="text-4xl" data-id="element-1584">{integration.logo}</div>
                <div data-id="element-1585">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-1586">
                    {integration.name}
                  </h3>
                  <p className="text-xs text-gray-600" data-id="element-1587">
                    {integration.category}
                  </p>
                </div>
              </div>
              {integration.requiredPlan !== 'Free' && <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded" data-id="element-1588">
                  {integration.requiredPlan}
                </span>}
            </div>
            <p className="text-sm text-gray-600 mb-4" data-id="element-1589">
              {integration.description}
            </p>
            <div className="flex flex-wrap gap-2 mb-4" data-id="element-1590">
              {integration.badges.map(badge => <span key={badge} className={`px-2 py-1 text-xs font-medium rounded border ${getBadgeColor(badge)}`} data-id="element-1591">
                  {badge}
                </span>)}
            </div>
            <div className="flex items-center gap-2" data-id="element-1592">
              <button className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium" data-id="element-1593">
                Connect
              </button>
              <button className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1594">
                <FileTextIcon className="w-4 h-4 text-gray-600" data-id="element-1595" />
              </button>
              <button className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1596">
                <EyeIcon className="w-4 h-4 text-gray-600" data-id="element-1597" />
              </button>
            </div>
          </div>)}
      </div>
    </div>;
}